'use client'

import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useParams, usePathname } from 'next/navigation'
import { axiosAuth, axiosPublic } from '@/utils/axios'
import { BASE_API_URL } from '@/constant/api'
import {
  createRum,
  instrumentFetch,
  QuickTrackingRumTransport,
} from 'rum-web-sdk'
import { instrumentAxios } from 'rum-web-sdk/plugins/axios'
import type { RumClient, RumEnv, RumSdk } from 'rum-web-sdk'
import type { RumAxiosPluginOptions } from 'rum-web-sdk/plugins/axios'
import { getRumRuntimeConfig } from '@/utils/rum'

const RumContext = createContext<RumSdk | null>(null)

type RouteParams = Record<string, string | string[]>
type TipsyApiResponseData = {
  code?: unknown
}

const rumTraceHeadersEnabled =
  process.env.NEXT_PUBLIC_RUM_TRACE_HEADERS_ENABLED === 'true'
const rumWebVitalsReportAllChanges =
  process.env.NEXT_PUBLIC_RUM_WEB_VITALS_REPORT_ALL_CHANGES === 'true'
const rumRuntimeConfig = getRumRuntimeConfig()

const LIKELY_DYNAMIC_SEGMENT_RE =
  /^(?:\d{4,}|[0-9a-f]{8,}(?:-[0-9a-f-]{4,})?|[A-Za-z0-9_-]{16,})$/i

/** 将环境变量收敛到 SDK 支持的环境枚举。 */
const getRumEnv = (): RumEnv => {
  const env = process.env.NEXT_PUBLIC_RUM_ENV || process.env.NODE_ENV
  if (env === 'test' || env === 'pre' || env === 'production') return env
  return 'development'
}

/**
 * 只允许给本站和业务 API origin 注入 trace header。
 *
 * 这样能避免第三方分析、Sentry、支付跳转等外部请求被 RUM header 污染。
 */
const getAllowedTracingOrigins = () => {
  const origins = new Set<string>()

  if (typeof window !== 'undefined') origins.add(window.location.origin)

  try {
    if (BASE_API_URL) origins.add(new URL(BASE_API_URL).origin)
  } catch {
    // Ignore invalid configuration and keep same-origin tracing.
  }

  return Array.from(origins)
}

const getParamSegments = (value: string | string[] | undefined) => {
  if (!value) return []
  return Array.isArray(value) ? value : [value]
}

const isLikelyDynamicSegment = (segment: string) =>
  LIKELY_DYNAMIC_SEGMENT_RE.test(segment)

const getTipsyApiResponseData = (value: unknown) => {
  if (!value || typeof value !== 'object') return undefined

  const responseLike = value as {
    data?: unknown
    response?: {
      data?: unknown
    }
  }

  return (responseLike.response?.data || responseLike.data) as
    | TipsyApiResponseData
    | undefined
}

const tipsyAxiosRumOptions: RumAxiosPluginOptions = {
  classifyResponse: (response) => {
    const code = getTipsyApiResponseData(response)?.code
    if (typeof code !== 'undefined' && code !== 0) return 'error'
    return undefined
  },
  getEventData: (value) => {
    const code = getTipsyApiResponseData(value)?.code
    if (typeof code === 'undefined') return undefined
    return {
      business_code: code,
    }
  },
}

/**
 * 将真实 path 收敛成低基数 route，避免 /chat/123 这类值撑爆看板维度。
 */
const normalizeRumRoute = (
  pathname: string | null,
  params: RouteParams
): string | undefined => {
  if (!pathname) return undefined

  const rawSegments = pathname.split('?')[0].split('/').filter(Boolean)
  const lang = getParamSegments(params.lang)[0]
  const segments =
    lang && rawSegments[0] === lang ? rawSegments.slice(1) : rawSegments
  const dynamicParams = Object.entries(params).filter(
    ([key, value]) => key !== 'lang' && getParamSegments(value).length > 0
  )
  const normalized: string[] = []

  for (let index = 0; index < segments.length; ) {
    let matched = false

    for (const [key, value] of dynamicParams) {
      const values = getParamSegments(value)
      const matches = values.every(
        (segment, offset) => segments[index + offset] === segment
      )

      if (matches) {
        normalized.push(Array.isArray(value) ? `[...${key}]` : `[${key}]`)
        index += values.length
        matched = true
        break
      }
    }

    if (!matched) {
      const segment = segments[index]
      normalized.push(isLikelyDynamicSegment(segment) ? '[id]' : segment)
      index += 1
    }
  }

  return `/${normalized.join('/')}`
}

/** Tipsy 项目的 RUM 接入层：创建 SDK、注册 axios/fetch、监听路由。 */
export function RumProvider({ children }: PropsWithChildren) {
  const pathname = usePathname()
  const params = useParams<RouteParams>()
  const route = useMemo(
    () => normalizeRumRoute(pathname, params),
    [params, pathname]
  )
  const rumRef = useRef<RumClient | null>(null)
  const rum = useMemo(() => {
    // React strict mode 下 useMemo 可能重新执行，ref 保证 SDK 单例稳定。
    if (rumRef.current) return rumRef.current

    rumRef.current = createRum({
      appId: process.env.NEXT_PUBLIC_RUM_APP_ID || 'tipsy-ui',
      env: getRumEnv(),
      endpoint: rumRuntimeConfig.endpoint,
      transport: rumRuntimeConfig.useQuickTrackingTransport
        ? new QuickTrackingRumTransport()
        : undefined,
      release: process.env.NEXT_PUBLIC_RUM_RELEASE,
      sampleRate: Number(process.env.NEXT_PUBLIC_RUM_SAMPLE_RATE || 1),
      enabled: rumRuntimeConfig.enabled,
      debugConsole: rumRuntimeConfig.debugConsole,
      traceHeadersEnabled: rumTraceHeadersEnabled,
      resourceSampleRate: Number(
        process.env.NEXT_PUBLIC_RUM_RESOURCE_SAMPLE_RATE ||
          (rumRuntimeConfig.debugConsole ? 1 : 0.1)
      ),
      webVitalsReportAllChanges: rumWebVitalsReportAllChanges,
      allowedTracingOrigins: getAllowedTracingOrigins(),
      // 崩溃恢复心跳与冷启动预取共用同一个合并根 SW（public/sw.js）。
      // 不再注册 /rum-sw.js，避免两个根 SW 互相 take over。
      crash: {
        serviceWorkerUrl: '/sw.js',
      },
      // 明确排除已有观测平台和 RUM 自己的上报请求。
      ignoreUrls: [
        /\/__nextjs_original-stack-frame/,
        /\/__nextjs_launch-editor/,
        /\/_next\//,
        /\/rum\/collect/,
        /quicktracking/i,
        /sentry/i,
        /vercel-insights/i,
      ],
      whiteScreen: {
        rootSelector: '[data-rum-content]',
        errorFallbackSelectors: ['[data-nextjs-error]', '.next-error-h1'],
      },
    })

    return rumRef.current
  }, [])

  useEffect(() => {
    // Provider 挂载时注册全局采集器，并在卸载时恢复 axios/fetch。
    rum.start()
    const disposeAuth = instrumentAxios(rum, axiosAuth, tipsyAxiosRumOptions)
    const disposePublic = instrumentAxios(
      rum,
      axiosPublic,
      tipsyAxiosRumOptions
    )
    const disposeFetch = instrumentFetch(rum)

    return () => {
      disposeFetch()
      disposeAuth()
      disposePublic()
      rum.stop()
    }
  }, [rum])

  useEffect(() => {
    // Next App Router 路由变化时开启新的 page trace 并上报 send_page_view。
    // 依赖真实 pathname，让 /chat/a -> /chat/b 这类低基数归一化后相同的页面也重置计时起点。
    rum.setRoute(route, { routeKey: pathname ?? undefined })
  }, [pathname, route, rum])

  return <RumContext.Provider value={rum}>{children}</RumContext.Provider>
}

export const useRum = () => useContext(RumContext)
